import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { CSSTransition } from "react-transition-group";
import { Modal, type Props as ModalProps } from "~/components/Modal/Modal";
import "./style.css";

export type ModalOption = {
	disableCloseOnClickSpace?: boolean;
};

const defaultOption: ModalOption = {};

export type ModalOpener = (props: ModalProps, option?: ModalOption) => void;

export type ModalCloser = () => void;

export const ModalContext = React.createContext<{
	open: ModalOpener;
	close: ModalCloser;
} | null>(null);

interface Props {
	children: ReactNode;
}

export const ModalProvider: React.FC<Props> = ({ children }) => {
	const [option, setOption] = useState<ModalOption>(defaultOption);
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [modalProps, setModalProps] = useState<ModalProps>({
		type: "info",
		title: "CONFIRM",
		children: <>CONFIRM</>,
	});
	const nodeRef = useRef(null);

	// Workaround to prevent endless loop
	const isOpenedRef = useRef<boolean>(false);
	useEffect(() => {
		isOpenedRef.current = isOpened;
	}, [isOpened]);

	const open = useCallback((props: ModalProps, option?: ModalOption) => {
		if (isOpenedRef.current) {
			return;
		}
		setOption(option ?? defaultOption);
		setModalProps(props);
		setIsOpened(true);
	}, []);

	const close = useCallback(() => {
		setIsOpened(false);
	}, []);

	const closeOnClickSpace = useCallback(() => {
		if (option.disableCloseOnClickSpace) return;
		setIsOpened(false);
	}, [option]);

	const escFunction = useCallback(
		(event: KeyboardEvent) => {
			if (isOpened && event.key === "Escape") {
				closeOnClickSpace();
			}
		},
		[isOpened, closeOnClickSpace],
	);
	useEffect(() => {
		document.addEventListener("keydown", escFunction, false);
		return () => {
			document.removeEventListener("keydown", escFunction, false);
		};
	}, [escFunction]);

	return (
		<ModalContext.Provider value={{ open, close }}>
			<CSSTransition
				in={isOpened}
				nodeRef={nodeRef}
				classNames="t4-modal-animation"
				timeout={200}
				unmountOnExit
			>
				{/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close is a UX enhancement; keyboard close handled by Escape key */}
				<div
					className="t4-modal-wrapper flex flex-col justify-center items-center"
					ref={nodeRef}
					role="presentation"
					onClick={(e) => {
						if (e.target === e.currentTarget) closeOnClickSpace();
					}}
				>
					<Modal {...modalProps} />
				</div>
			</CSSTransition>
			{children}
		</ModalContext.Provider>
	);
};
